import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { Button, Checkbox, Form, Message, Radio } from "semantic-ui-react";

import { validateEmail } from "../../utils/validation";

// react-hook-form + Semantic UI, matching RegisterForm / CustomerCalculatorForm (the repo's
// actual form convention). Posts to the public /api/bulk-inquiry endpoint. No documents are
// sent from here — they're delivered by hand after review.

interface BulkInquiryFields {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  fulfillment: "" | "delivery" | "self-service";
  deliveryLocation: string;
  estimatedVolume: string;
  requestedDocs: string[];
  message: string;
  website: string; // honeypot — must stay empty
}

const DOC_OPTIONS = [
  { value: "title22", label: "Title 22 drinking-water testing" },
  { value: "title21", label: "Title 21 finished-product analysis" },
];

// Fire analytics only if the site's analytics layer is present. The google-analytics work
// (PR #268) isn't in main yet; when it merges, import trackEvent from utils/analytics and
// call it here. Until then this safely no-ops.
const trackBulkInquirySubmitted = () => {
  const w = window as unknown as { trackEvent?: (event: string) => void };
  if (typeof w.trackEvent === "function") {
    w.trackEvent("bulk_inquiry_submitted");
  }
};

const BulkInquiryForm: React.FC = () => {
  const renderedAtRef = useRef<number>(Date.now());
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BulkInquiryFields>({
    mode: "onTouched",
    defaultValues: {
      companyName: "",
      contactName: "",
      email: "",
      phone: "",
      fulfillment: "",
      deliveryLocation: "",
      estimatedVolume: "",
      requestedDocs: [],
      message: "",
      website: "",
    },
  });

  useEffect(() => {
    register("companyName", { required: "Company or organization is required." });
    register("contactName", { required: "Your name is required." });
    register("email", {
      required: "Email is required.",
      validate: (v) => !!validateEmail(v) || "Please enter a valid email address.",
    });
    register("phone");
    register("fulfillment", { required: "Please choose how you would get the water." });
    register("deliveryLocation", {
      // Required only when delivery is chosen.
      validate: (v) =>
        watch("fulfillment") !== "delivery" ||
        (typeof v === "string" && v.trim().length > 0) ||
        "Please tell us where the water would be delivered.",
    });
    register("estimatedVolume");
    register("requestedDocs");
    register("message");
    register("website");
  }, [register, watch]);

  const fulfillment = watch("fulfillment");
  const requestedDocs = watch("requestedDocs") || [];

  const toggleDoc = (value: string) => {
    const next = requestedDocs.includes(value)
      ? requestedDocs.filter((d) => d !== value)
      : [...requestedDocs, value];
    setValue("requestedDocs", next);
  };

  const onSubmit = async (data: BulkInquiryFields) => {
    setServerError(false);
    try {
      const res = await axios.post("/api/bulk-inquiry", {
        ...data,
        sourceUrl: typeof window !== "undefined" ? window.location.href : undefined,
        renderedAt: renderedAtRef.current,
      });
      if (res.status === 201 && res.data && res.data.ok) {
        trackBulkInquirySubmitted();
        setSubmitted(true);
      } else {
        setServerError(true);
      }
    } catch (err) {
      setServerError(true);
    }
  };

  if (submitted) {
    return (
      <div className="wq-thanks">
        <h3>Thanks — we've got it.</h3>
        <p>
          We'll be in touch within one business day with the documents you asked for and the
          details on your request.
        </p>
      </div>
    );
  }

  return (
    <div className="wq-formcard">
      <Form onSubmit={handleSubmit(onSubmit)} error={serverError}>
        <Form.Group widths="equal">
          <Form.Input
            fluid
            required
            label="Company / organization"
            placeholder="Company / organization"
            value={watch("companyName")}
            onChange={(_e, data) => setValue("companyName", data.value, { shouldValidate: true })}
            error={
              errors.companyName
                ? { content: errors.companyName.message || "Required", pointing: "above" }
                : undefined
            }
          />
          <Form.Input
            fluid
            required
            label="Your name"
            placeholder="Your name"
            value={watch("contactName")}
            onChange={(_e, data) => setValue("contactName", data.value, { shouldValidate: true })}
            error={
              errors.contactName
                ? { content: errors.contactName.message || "Required", pointing: "above" }
                : undefined
            }
          />
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Input
            fluid
            required
            type="email"
            label="Email"
            placeholder="Email"
            value={watch("email")}
            onChange={(_e, data) => setValue("email", data.value, { shouldValidate: true })}
            error={
              errors.email
                ? { content: errors.email.message || "Please enter a valid email address", pointing: "above" }
                : undefined
            }
          />
          <Form.Input
            fluid
            type="tel"
            label="Phone (optional)"
            placeholder="Phone"
            value={watch("phone")}
            onChange={(_e, data) => setValue("phone", data.value)}
          />
        </Form.Group>

        <Form.Field required error={Boolean(errors.fulfillment)}>
          <label>How would you get the water?</label>
          <Form.Field>
            <Radio
              label="Deliver it to me"
              name="fulfillment"
              checked={fulfillment === "delivery"}
              onChange={() => setValue("fulfillment", "delivery", { shouldValidate: true })}
            />
          </Form.Field>
          <Form.Field>
            <Radio
              label="I'll fill my own tanker at the Acton yard"
              name="fulfillment"
              checked={fulfillment === "self-service"}
              onChange={() => setValue("fulfillment", "self-service", { shouldValidate: true })}
            />
          </Form.Field>
          {errors.fulfillment && (
            <div className="wq-field-error">{errors.fulfillment.message}</div>
          )}
        </Form.Field>

        {fulfillment === "delivery" && (
          <Form.Input
            fluid
            required
            label="Where would the water be delivered?"
            placeholder="City or area"
            value={watch("deliveryLocation")}
            onChange={(_e, data) => setValue("deliveryLocation", data.value, { shouldValidate: true })}
            error={
              errors.deliveryLocation
                ? { content: errors.deliveryLocation.message || "Required", pointing: "above" }
                : undefined
            }
          />
        )}

        <Form.Input
          fluid
          label="Estimated volume or frequency (optional)"
          placeholder="e.g. weekly delivery"
          value={watch("estimatedVolume")}
          onChange={(_e, data) => setValue("estimatedVolume", data.value)}
        />

        <Form.Field>
          <label>What can we send you? (optional)</label>
          {DOC_OPTIONS.map((opt) => (
            <Checkbox
              key={opt.value}
              label={opt.label}
              style={{ display: "block", marginTop: 8 }}
              checked={requestedDocs.includes(opt.value)}
              onChange={() => toggleDoc(opt.value)}
            />
          ))}
        </Form.Field>

        <Form.TextArea
          label="Anything else we should know? (optional)"
          value={watch("message")}
          onChange={(_e, data) => setValue("message", String(data.value ?? ""))}
        />

        {/* Honeypot — hidden from users; a bot that fills it is silently dropped server-side. */}
        <input
          className="wq-honeypot"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={watch("website")}
          onChange={(e) => setValue("website", e.target.value)}
        />

        <Button type="submit" primary style={{ backgroundColor: "#2185d0" }} disabled={isSubmitting}>
          Request information
        </Button>

        <Message
          error
          header="Something went wrong"
          content="We couldn't submit your request. Please try again, or call (661) 566-4426."
        />
      </Form>
    </div>
  );
};

export default BulkInquiryForm;
